'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { ArticleDto, articlesApi } from '@/app/api/articles';
import { categoriesApi, Category } from '@/app/api/categories';
import { PlusIcon, DocumentTextIcon, ArrowPathIcon, DocumentArrowUpIcon } from '@heroicons/react/24/outline';
import ArticlesList from './ArticlesList';
import ArticleDetails from './ArticleDetails';
import ArticlesToolbar from './ArticlesToolbar';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { ArticleWizardModal, AIGeneratedArticle, ArticlePlanningData } from '../create/components';
import { generateMarkdownContent } from '../utils/content-parser';
import { ArticleMeta, ArticleMetadata } from '../utils/article-meta';
import ArticlePublishWizard from './ArticlePublishWizard';
import { useCreateArticleAction } from '@/hooks/useCreateArticleAction';
import { usePublishArticle } from '@/hooks/usePublishArticle';
import ImportArticleModal, { ImportedContent } from './ImportArticleModal';
import ShareArticleModal from './ShareArticleModal';
import FeaturedSummaryWizard from './FeaturedSummaryWizard';
import { apiClient } from '@/app/api/client';

// Article interface that maps to DocumentDto from the backend
export interface Publication {
  platformName: string;
  iconUrl: string;
  status: 'published' | 'in_progress' | 'pending' | 'failed';
  externalUrl?: string;
  errorMessage?: string;
}

export interface Article {
  id: string; // uuid from DocumentDto
  title: string; // from meta.filename or meta.title
  status: string; // state from DocumentDto
  author: string; // from meta.author or user info
  category: string; // from meta.category or tags
  publishDate?: string; // from meta.publishDate
  views?: number; // from meta.views
  lastModified: number; // from updatedAt or contentUpdatedAt
  tags?: string[]; // document tags
  slug?: string; // from slug field for published articles
  meta: { // Pass through the meta object from the DTO
    summary?: string;
    [key: string]: any;
  };
  publications?: Publication[];
}

export default function ArticlesManagement() {
  useCreateArticleAction();
  const navigate = useNavigate();
  const [articles, setArticles] = useState<Article[]>([]);
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterState, setFilterState] = useState<string | undefined>('active'); // Default to active (draft + published)
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'published'>('all');
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [categoryMap, setCategoryMap] = useState<Record<string, string>>({});
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState<string | null>(null);
  const [publishConfirmState, setPublishConfirmState] = useState<{ isOpen: boolean; articleId: string | null }>({ isOpen: false, articleId: null });
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();


  // State for wizard and article creation
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generatedArticle, setGeneratedArticle] = useState<AIGeneratedArticle | null>(null);
  const [wizardFormData, setWizardFormData] = useState<ArticlePlanningData | null>(null);

  // State for import modal
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [articleToShare, setArticleToShare] = useState<Article | null>(null);

  // State for featured summary wizard
  const [isFeaturedSummaryOpen, setIsFeaturedSummaryOpen] = useState(false);
  const [articleForFeaturedSummary, setArticleForFeaturedSummary] = useState<Article | null>(null);

  // Use publish hook for unified publish logic
  const publishHook = usePublishArticle({
    onSuccess: () => {
      fetchArticles();
      if (selectedArticle) {
        setSelectedArticle(null);
      }
    },
    onError: (error) => {
      setError(error.message);
    }
  });

  // Apply filters (search query, category, and tags)
  const applyFilters = useCallback((
    articlesToFilter: Article[],
    query: string,
    category: string | undefined,
    tags: string[],
    status: 'all' | 'draft' | 'published'
  ) => {
    let filtered = articlesToFilter;

    // Apply status filter within the active tab (draft + published)
    if (status !== 'all') {
      filtered = filtered.filter(article => article.status === status);
    }

    // Apply search query filter
    if (query) {
      filtered = filtered.filter(article =>
        article.title.toLowerCase().includes(query.toLowerCase()) ||
        article.author.toLowerCase().includes(query.toLowerCase()) ||
        article.category.toLowerCase().includes(query.toLowerCase())
      );
    }

    // Apply category filter
    if (category) {
      filtered = filtered.filter(article =>
        article.category === category
      );
    }

    // Apply tags filter
    if (tags.length > 0) {
      filtered = filtered.filter(article =>
        tags.every(tag => article.tags?.includes(tag))
      );
    }

    setFilteredArticles(filtered);
  }, []);

  const pageSize = 10;

  const totalPages = useMemo(() => {
    return filteredArticles.length > 0
      ? Math.ceil(filteredArticles.length / pageSize)
      : 1;
  }, [filteredArticles.length]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedArticles = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredArticles.slice(startIndex, startIndex + pageSize);
  }, [filteredArticles, currentPage]);

  // Function to fetch articles from the backend
  const fetchArticles = useCallback(async () => {
    setIsLoading(true);
    try {
      // Fetch articles and all available document tags in parallel
      const [documents, allAvailableTags] = await Promise.all([
        articlesApi.getAll(filterState === 'archived' ? 'archived' : undefined),
        articlesApi.getAllTags('documents')
      ]);

      setAvailableTags(allAvailableTags);

      // Map DocumentDto to Article interface, including fetching specific tags for each article
      const documentsWithTags = await Promise.all(documents.map(async (doc: ArticleDto) => {
        const meta = doc.meta || {};
        const tags = await articlesApi.getArticleTags(doc.uuid);

        return {
          id: doc.uuid,
          title: meta.title || meta.filename || 'Untitled Document',
          status: doc.state || 'draft',
          author: meta.author || (user ? user.email : 'Unknown'),
          category: meta.category || '',
          publishDate: meta.publishDate,
          views: meta.views,
          lastModified: Math.max(
            doc.contentUpdatedAt ? parseInt(doc.contentUpdatedAt, 10) : 0,
            doc.updatedAt ? parseInt(doc.updatedAt, 10) : 0
          ),
          tags: tags,
          slug: doc.slug, // Include slug for published articles
          meta: meta
        };
      }));

      // For the combined "active" view, show all non-archived articles (draft + published).
      // Archived tab explicitly shows only archived articles.
      const visibleArticles = filterState === 'archived'
        ? documentsWithTags.filter(article => article.status === 'archived')
        : documentsWithTags.filter(article => article.status !== 'archived');

      setArticles(visibleArticles);
      applyFilters(
        visibleArticles,
        searchQuery,
        selectedCategory,
        selectedTags,
        filterState === 'active' ? statusFilter : 'all'
      );

      // Initialize articles without publication status - will be loaded individually when needed

    } catch (error) {
      console.error('Error fetching documents:', error);
      setError('Failed to fetch articles. Please try refreshing the page.');
    } finally {
      setIsLoading(false);
    }
  }, [filterState, searchQuery, selectedCategory, selectedTags, statusFilter, user, applyFilters]);

  // Load categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categories = await categoriesApi.getCategories();
        const map: Record<string, string> = {};
        categories.forEach(cat => {
          map[cat.name] = cat.display_name;
        });
        setCategoryMap(map);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    loadCategories();
  }, []);

  // Fetch articles on component mount and when filters change
  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  // Handle search query change
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
    applyFilters(
      articles,
      query,
      selectedCategory,
      selectedTags,
      filterState === 'active' ? statusFilter : 'all'
    );
  };

  // Handle category selection change
  const handleCategoryChange = (category: string | undefined) => {
    setSelectedCategory(category);
    setCurrentPage(1);
    applyFilters(
      articles,
      searchQuery,
      category,
      selectedTags,
      filterState === 'active' ? statusFilter : 'all'
    );
  };

  // Handle tag selection change
  const handleTagsChange = (tags: string[]) => {
    setSelectedTags(tags);
    setCurrentPage(1);
    applyFilters(
      articles,
      searchQuery,
      selectedCategory,
      tags,
      filterState === 'active' ? statusFilter : 'all'
    );
  };

  // Handle state filter change
  const handleStateFilterChange = (state: string | undefined) => {
    setFilterState(state);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (status: 'all' | 'draft' | 'published') => {
    setStatusFilter(status);
    setCurrentPage(1);
    applyFilters(
      articles,
      searchQuery,
      selectedCategory,
      selectedTags,
      filterState === 'active' ? status : 'all'
    );
  };

  // Handle updating tags for a single article in the list
  const handleUpdateArticleTags = async (articleId: string, newTags: string[]) => {
    // First, persist to backend
    await articlesApi.setArticleTags(articleId, newTags);

    // Then update local state
    const updateState = (setter: React.Dispatch<React.SetStateAction<Article[]>>) => {
      setter(prevArticles =>
        prevArticles.map(article =>
          article.id === articleId ? { ...article, tags: newTags } : article
        )
      );
    };
    updateState(setArticles);
    updateState(setFilteredArticles);
  };

  // Handle updating category for a single article in the list
  const handleUpdateArticleCategory = (articleId: string, newCategory: string) => {
    const updateState = (setter: React.Dispatch<React.SetStateAction<Article[]>>) => {
      setter(prevArticles =>
        prevArticles.map(article =>
          article.id === articleId ? { ...article, category: newCategory } : article
        )
      );
    };
    updateState(setArticles);
    updateState(setFilteredArticles);
  };

  // Handle refresh - fetch articles from backend
  const handleRefresh = () => {
    setCurrentPage(1);
    fetchArticles();
  };

  // Handle create new article
  const handleCreateArticle = () => {
    setIsWizardOpen(true);
  };

  // Handle import article - pure frontend, no backend calls
  const handleImportArticle = (importedContent: ImportedContent) => {
    // Convert HTML to Markdown if needed
    let content = importedContent.content;
    if (importedContent.format === 'html') {
      content = convertHtmlToMarkdown(importedContent.content);
    }

    // Check content size (sessionStorage limit is ~5-10MB)
    const dataToStore = {
      importedTitle: importedContent.title,
      importedContent: content,
      importedFilename: importedContent.filename,
      isImported: true
    };

    const dataString = JSON.stringify(dataToStore);
    const sizeInMB = new Blob([dataString]).size / (1024 * 1024);

    // If content is too large (>4MB), show warning
    if (sizeInMB > 4) {
      setError(`Content is too large (${sizeInMB.toFixed(2)}MB). Maximum size is 4MB. Please try a smaller file or split the content.`);
      return;
    }

    try {
      // Store imported content in sessionStorage (temporary, cleared on tab close)
      const importId = `import_${Date.now()}`;
      sessionStorage.setItem(importId, dataString);

      console.log('[Import] Stored data in sessionStorage:', {
        importId,
        dataSize: sizeInMB.toFixed(2) + 'MB',
        title: importedContent.title,
        contentLength: content.length
      });

      // Open create page in new tab with import ID
      const createUrl = `/articles/create?start=blank&import=${importId}`;
      window.open(createUrl, '_blank');
    } catch (err) {
      // Handle QuotaExceededError
      if (err instanceof Error && err.name === 'QuotaExceededError') {
        setError('Storage quota exceeded. Please try a smaller file or clear your browser cache.');
      } else {
        setError('Failed to store imported content. Please try again.');
      }
      console.error('Failed to store import data:', err);
    }
  };

  // Simple HTML to Markdown converter
  const convertHtmlToMarkdown = (html: string): string => {
    let markdown = html;

    // Remove script and style tags
    markdown = markdown.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    markdown = markdown.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '');

    // Convert headings
    markdown = markdown.replace(/<h1[^>]*>(.*?)<\/h1>/gi, '# $1\n\n');
    markdown = markdown.replace(/<h2[^>]*>(.*?)<\/h2>/gi, '## $1\n\n');
    markdown = markdown.replace(/<h3[^>]*>(.*?)<\/h3>/gi, '### $1\n\n');
    markdown = markdown.replace(/<h4[^>]*>(.*?)<\/h4>/gi, '#### $1\n\n');
    markdown = markdown.replace(/<h5[^>]*>(.*?)<\/h5>/gi, '##### $1\n\n');
    markdown = markdown.replace(/<h6[^>]*>(.*?)<\/h6>/gi, '###### $1\n\n');

    // Convert paragraphs
    markdown = markdown.replace(/<p[^>]*>(.*?)<\/p>/gi, '$1\n\n');

    // Convert bold and italic
    markdown = markdown.replace(/<(strong|b)[^>]*>(.*?)<\/\1>/gi, '**$2**');
    markdown = markdown.replace(/<(em|i)[^>]*>(.*?)<\/\1>/gi, '*$2*');

    // Convert links
    markdown = markdown.replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi, '[$2]($1)');

    // Convert images
    markdown = markdown.replace(/<img[^>]*src="([^"]*)"[^>]*alt="([^"]*)"[^>]*>/gi, '![$2]($1)');
    markdown = markdown.replace(/<img[^>]*src="([^"]*)"[^>]*>/gi, '![]($1)');

    // Convert lists
    markdown = markdown.replace(/<ul[^>]*>(.*?)<\/ul>/gis, (match, content) => {
      return content.replace(/<li[^>]*>(.*?)<\/li>/gi, '- $1\n');
    });
    markdown = markdown.replace(/<ol[^>]*>(.*?)<\/ol>/gis, (match, content) => {
      let counter = 1;
      return content.replace(/<li[^>]*>(.*?)<\/li>/gi, () => `${counter++}. $1\n`);
    });

    // Convert code blocks
    markdown = markdown.replace(/<pre[^>]*><code[^>]*>(.*?)<\/code><\/pre>/gis, '```\n$1\n```\n\n');
    markdown = markdown.replace(/<code[^>]*>(.*?)<\/code>/gi, '`$1`');

    // Convert line breaks
    markdown = markdown.replace(/<br\s*\/?>/gi, '\n');

    // Remove remaining HTML tags
    markdown = markdown.replace(/<[^>]+>/g, '');

    // Clean up extra whitespace
    markdown = markdown.replace(/\n{3,}/g, '\n\n');
    markdown = markdown.trim();

    return markdown;
  };

  const handleWizardClose = () => {
    setIsWizardOpen(false);
  };

  const handleWizardComplete = (article: AIGeneratedArticle, formData: ArticlePlanningData) => {
    setGeneratedArticle(article);
    setWizardFormData(formData);
    setIsWizardOpen(false);
    // Trigger article creation after wizard completion
    createArticle(article, formData);
  };


  const createArticle = async (article: AIGeneratedArticle, formData: ArticlePlanningData) => {
    if (!user || !article) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Generate markdown content from the AI-generated article
      const markdownContent = generateMarkdownContent(article);

      // Prepare metadata
      const metadata: ArticleMeta = {
        title: article.selectedTitle,
        author: user.email,
        category: formData.category,
        status: 'draft',
        tags: article.tags,
        publicationDate: new Date().toISOString(),
        aiContext: formData,
      };

      // Create the article with metadata only
      const newArticle = await articlesApi.createArticle(metadata);

      // Prime the durable object with the initial content
      await articlesApi.updateArticleContent(newArticle.uuid, markdownContent);

      // Set tags for the new article
      if (article.tags && article.tags.length > 0) {
        await articlesApi.setArticleTags(newArticle.uuid, article.tags);
      }

      // Open the new article in a new tab for editing
      window.open(`/articles/edit?id=${newArticle.uuid}`, '_blank');
      // Refresh the articles list
      fetchArticles();

    } catch (err: any) {
      console.error('Failed to create article:', err);
      setError(err.message || 'An unexpected error occurred while creating the article.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle edit article
  const handleEditArticle = (id: string) => {
    // Open in a new tab/window with opener relationship preserved
    // This allows the new tab to close itself when clicking Cancel
    const newWindow = window.open(`/articles/edit?id=${id}`, '_blank');
    // Note: Modern browsers may still set opener to null for security
    // But this gives the new tab the ability to close itself
  };

  // Handle delete article
  const handleDeleteArticle = (id: string) => {
    setArticleToDelete(id);
    setIsDeleteDialogOpen(true);
  };

  // Handle delete confirmation
  const confirmDeleteArticle = async () => {
    if (articleToDelete) {
      setIsLoading(true);
      try {
        await articlesApi.deleteArticle(articleToDelete);
        // Refresh the list after deletion
        fetchArticles();
      } catch (error) {
        console.error('Error deleting document:', error);
        alert('Failed to delete article. Please try again.');
      } finally {
        setIsLoading(false);
        // Close the dialog and reset the article to delete
        setIsDeleteDialogOpen(false);
        setArticleToDelete(null);
      }
    }
  };

  // Handle cancel delete
  const cancelDeleteArticle = () => {
    setIsDeleteDialogOpen(false);
    setArticleToDelete(null);
  };

  // Handle Publish Article - use unified hook
  const handlePublish = async (id: string) => {
    const article = articles.find(a => a.id === id);
    if (!article) return;

    const hasCategory = !!(article.category && article.category.trim().length > 0);
    const hasTags = Array.isArray(article.tags) && article.tags.length > 0;

    if (!hasCategory || !hasTags) {
      setPublishConfirmState({ isOpen: true, articleId: id });
      return;
    }

    setError(null);
    await publishHook.initiatePublish(id);
  };

  const handleConfirmPublishWithoutMeta = async () => {
    if (!publishConfirmState.articleId) return;
    setError(null);
    await publishHook.initiatePublish(publishConfirmState.articleId);
  };

  // Handle Unpublish Article
  const handleUnpublish = async (id: string) => {
    try {
      await articlesApi.unpublishArticle(id);
      fetchArticles();
      if (selectedArticle && selectedArticle.id === id) {
        setSelectedArticle(null);
      }
    } catch (err) {
      console.error('Failed to unpublish article:', err);
      setError('An error occurred while unpublishing the article.');
    }
  };

  // Handle View Public Article
  const handleViewPublic = (slug: string) => {
    if (slug) {
      window.open(`/public/articles/${slug}`, '_blank');
    }
  };

  const handleShareArticle = (id: string) => {
    const article = articles.find(a => a.id === id);
    if (article) {
      setArticleToShare(article);
      setIsShareModalOpen(true);
    }
  };

  // Handle featured summary wizard
  const handleFeaturedSummary = (article: Article) => {
    setArticleForFeaturedSummary(article);
    setIsFeaturedSummaryOpen(true);
  };

  const handleFeaturedSummarySave = (summary: string, layout: 'vertical' | 'horizontal', ctaText: string) => {
    // Update local article data with new featured summary
    if (articleForFeaturedSummary) {
      const updateState = (setter: React.Dispatch<React.SetStateAction<Article[]>>) => {
        setter(prevArticles =>
          prevArticles.map(a =>
            a.id === articleForFeaturedSummary.id
              ? { ...a, meta: { ...a.meta, featuredSummary: summary, featuredSummaryLayout: layout, featuredCtaText: ctaText } }
              : a
          )
        );
      };
      updateState(setArticles);
      updateState(setFilteredArticles);
    }
    setIsFeaturedSummaryOpen(false);
    setArticleForFeaturedSummary(null);
  };


  // Handle article selection
  const handleArticleSelect = (article: Article) => {
    setSelectedArticle(article);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="flex items-center">
              <DocumentTextIcon className="h-8 w-8 text-blue-600 mr-3" />
              <h1 className="text-2xl font-bold text-gray-900">Articles</h1>
            </div>
            <div className="flex items-center space-x-2 overflow-x-auto pb-1 md:pb-0">
              <button
                type="button"
                onClick={handleRefresh}
                className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 md:px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 whitespace-nowrap"
                disabled={isLoading}
              >
                <ArrowPathIcon className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} aria-hidden="true" />
                <span className="ml-2 hidden sm:inline">{isLoading ? 'Refreshing...' : 'Refresh'}</span>
              </button>
              <button
                type="button"
                onClick={() => setIsImportModalOpen(true)}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 md:px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 whitespace-nowrap"
                disabled={isSubmitting}
              >
                <DocumentArrowUpIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                <span className="hidden sm:inline">Import Article</span>
                <span className="sm:hidden">Import</span>
              </button>
              <button
                type="button"
                onClick={handleCreateArticle}
                className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-3 py-2 md:px-4 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 whitespace-nowrap"
                disabled={isSubmitting}
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                <span className="hidden sm:inline">{isSubmitting ? 'Creating...' : 'Create New Article'}</span>
                <span className="sm:hidden">Create</span>
              </button>
            </div>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Toolbar with filters */}
        <ArticlesToolbar
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          filterState={filterState}
          onFilterStateChange={handleStateFilterChange}
          availableCategories={Object.entries(categoryMap).map(([value, label]) => ({ value, label }))}
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
          availableTags={availableTags}
          selectedTags={selectedTags}
          onTagsChange={handleTagsChange}
          isLoading={isLoading}
          statusFilter={statusFilter}
          onStatusFilterChange={handleStatusFilterChange}
        />

        {/* Main content */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <ArticlesList
            articles={paginatedArticles}
            selectedArticle={selectedArticle}
            onArticleSelect={handleArticleSelect}
            onEditArticle={handleEditArticle}
            onDeleteArticle={handleDeleteArticle}
            onPublishArticle={handlePublish}
            onShareArticle={handleShareArticle}
            onFeaturedSummary={handleFeaturedSummary}
            isLoading={isLoading}
            onRefresh={fetchArticles}
            availableTags={availableTags}
            onUpdateArticleTags={handleUpdateArticleTags}
            onUpdateArticleCategory={handleUpdateArticleCategory}
            categoryMap={categoryMap}
          />
          {!isLoading && filteredArticles.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <p className="text-sm text-gray-700">
                {(() => {
                  const start = (currentPage - 1) * pageSize + 1;
                  const end = Math.min(currentPage * pageSize, filteredArticles.length);
                  return `Showing ${start} to ${end} of ${filteredArticles.length} articles`;
                })()}
              </p>
              <div className="inline-flex rounded-md shadow-sm" role="group">
                <button
                  type="button"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className={`px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-l-md
                    ${currentPage === 1
                      ? 'text-gray-300 bg-white cursor-not-allowed'
                      : 'text-gray-700 bg-white hover:bg-gray-50'}`}
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1.5 text-sm font-medium border border-gray-300 border-l-0 rounded-r-md
                    ${currentPage === totalPages
                      ? 'text-gray-300 bg-white cursor-not-allowed'
                      : 'text-gray-700 bg-white hover:bg-gray-50'}`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Confirmation Dialog for Delete */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        title="Delete Article"
        message="Are you sure you want to delete this article? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonClassName="bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500"
        onConfirm={confirmDeleteArticle}
        onClose={cancelDeleteArticle}
      />

      <ConfirmDialog
        isOpen={publishConfirmState.isOpen}
        title="Publish without category or tags?"
        message="Please select a category and at least one tag before publishing an article."
        confirmText="Continue Publishing"
        cancelText="Cancel"
        confirmButtonClassName="bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-500"
        onConfirm={handleConfirmPublishWithoutMeta}
        onClose={() => setPublishConfirmState({ isOpen: false, articleId: null })}
      />

      {/* Article Creation Wizard */}
      {isWizardOpen && (
        <ArticleWizardModal
          isOpen={isWizardOpen}
          onClose={handleWizardClose}
          onComplete={handleWizardComplete}
          initialFormData={wizardFormData || undefined}
          onFormDataChange={setWizardFormData}
        />
      )}

      {/* Import Article Modal */}
      <ImportArticleModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImport={handleImportArticle}
      />

      {/* Unified Article Publish Wizard */}
      {publishHook.isWizardOpen && (
        <ArticlePublishWizard
          isOpen={publishHook.isWizardOpen}
          onClose={publishHook.closeWizard}
          onPublish={publishHook.handleWizardPublish}
          articleId={publishHook.currentArticleId || ''}
          articleTitle={publishHook.currentArticleTitle}
          initialMeta={publishHook.wizardInitialMeta}
          articleContent={publishHook.articleContent}
          availableTags={availableTags.map(t => ({ value: t, label: t }))}
          availableCategories={Object.entries(categoryMap).map(([value, label]) => ({ value, label }))}
          isPublishing={publishHook.isPublishing}
        />
      )}

      {/* Share Article Modal */}
      <ShareArticleModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        article={articleToShare}
      />

      {/* Featured Summary Wizard */}
      {articleForFeaturedSummary && (
        <FeaturedSummaryWizard
          isOpen={isFeaturedSummaryOpen}
          onClose={() => {
            setIsFeaturedSummaryOpen(false);
            setArticleForFeaturedSummary(null);
          }}
          onSave={handleFeaturedSummarySave}
          articleId={articleForFeaturedSummary.id}
          articleTitle={articleForFeaturedSummary.title}
          existingSummary={articleForFeaturedSummary.meta?.featuredSummary || ''}
          existingLayout={(articleForFeaturedSummary.meta?.featuredSummaryLayout as any) || 'vertical'}
          existingCtaText={articleForFeaturedSummary.meta?.featuredCtaText || 'Read full story'}
        />
      )}

    </div>
  );
}
